import React, { useEffect, useState } from 'react';

import Modal from '../components/Modal';
import featureService from '../services/featureService';
import type {
  Feature,
  FeatureCreate,
  FeatureGroup,
  FeatureGroupCreate,
  FeatureGroupUpdate,
  FeatureUpdate,
} from '../types';

type FeatureTab = 'rows' | 'groups';
type TranslationMap = Record<string, string>;

const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'German' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'pt-br', label: 'Portuguese (Brazil)' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
] as const;

function normalizeTranslations(translations?: TranslationMap): TranslationMap {
  if (!translations) {
    return {};
  }

  return Object.entries(translations).reduce<TranslationMap>((accumulator, [language, value]) => {
    if (typeof value === 'string' && value.trim()) {
      accumulator[language] = value.trim();
    }
    return accumulator;
  }, {});
}

function summarizeTranslations(translations?: TranslationMap): string {
  const languages = Object.keys(normalizeTranslations(translations));
  return languages.length > 0 ? languages.join(', ') : 'No translations';
}

interface TranslationFieldsProps {
  title: string;
  value: TranslationMap;
  onChange: (value: TranslationMap) => void;
  placeholder: string;
}

function TranslationFields({
  title,
  value,
  onChange,
  placeholder,
}: TranslationFieldsProps) {
  return (
    <div className="translation-block">
      <h3>{title}</h3>
      <div className="translation-grid">
        {SUPPORTED_LANGUAGES.map((language) => (
          <label key={language.code} className="translation-input">
            <span>{language.label}</span>
            <input
              type="text"
              className="form-control"
              value={value[language.code] ?? ''}
              onChange={(event) =>
                onChange({
                  ...value,
                  [language.code]: event.target.value,
                })
              }
              placeholder={`${placeholder} (${language.code})`}
            />
          </label>
        ))}
      </div>
    </div>
  );
}

interface FeatureGroupFormProps {
  featureGroup?: FeatureGroup;
  onSubmit: (payload: FeatureGroupCreate | FeatureGroupUpdate) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
}

function FeatureGroupForm({
  featureGroup,
  onSubmit,
  onCancel,
  loading,
}: FeatureGroupFormProps) {
  const [key, setKey] = useState(featureGroup?.key ?? '');
  const [label, setLabel] = useState(featureGroup?.label ?? '');
  const [sortOrder, setSortOrder] = useState(featureGroup?.sort_order ?? 0);
  const [isActive, setIsActive] = useState(featureGroup?.is_active ?? true);
  const [labelTranslations, setLabelTranslations] = useState<TranslationMap>(
    featureGroup?.label_translations ?? {},
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const nextErrors: Record<string, string> = {};
    if (!key.trim()) {
      nextErrors.key = 'Key is required.';
    }
    if (!label.trim()) {
      nextErrors.label = 'Label is required.';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    await onSubmit({
      key: key.trim(),
      label: label.trim(),
      label_translations: normalizeTranslations(labelTranslations),
      sort_order: Number(sortOrder || 0),
      is_active: isActive,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="details-form">
      <div className="details-section">
        <h2>Feature Group Details</h2>
        <div className="details-grid">
          <label className="form-group">
            <span className="form-label">Group Key *</span>
            <input
              type="text"
              className={`form-control ${errors.key ? 'error' : ''}`}
              value={key}
              onChange={(event) => setKey(event.target.value)}
              placeholder="sections.documentGenerator"
            />
            {errors.key && <small className="form-error">{errors.key}</small>}
          </label>

          <label className="form-group">
            <span className="form-label">Default Label *</span>
            <input
              type="text"
              className={`form-control ${errors.label ? 'error' : ''}`}
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="Document Generator"
            />
            {errors.label && <small className="form-error">{errors.label}</small>}
          </label>

          <label className="form-group">
            <span className="form-label">Display Order</span>
            <input
              type="number"
              className="form-control"
              min={0}
              value={sortOrder}
              onChange={(event) => setSortOrder(Number(event.target.value || 0))}
            />
          </label>

          <label className="form-group checkbox-row">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
            />
            <span>Feature group is active</span>
          </label>
        </div>
      </div>

      <div className="details-section">
        <h2>Translations</h2>
        <TranslationFields
          title="Group Label Translations"
          value={labelTranslations}
          onChange={setLabelTranslations}
          placeholder="Translated group label"
        />
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
          Close
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Saving...' : featureGroup ? 'Save Feature Group' : 'Create Feature Group'}
        </button>
      </div>
    </form>
  );
}

interface FeatureRowFormProps {
  feature?: Feature;
  featureGroups: FeatureGroup[];
  onSubmit: (payload: FeatureCreate | FeatureUpdate) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
}

function FeatureRowForm({
  feature,
  featureGroups,
  onSubmit,
  onCancel,
  loading,
}: FeatureRowFormProps) {
  const [featureName, setFeatureName] = useState(feature?.feature_name ?? '');
  const [displayName, setDisplayName] = useState(feature?.display_name ?? '');
  const [description, setDescription] = useState(feature?.description ?? '');
  const [comparisonKey, setComparisonKey] = useState(feature?.comparison_key ?? '');
  const [featureGroupId, setFeatureGroupId] = useState<number | ''>(
    feature?.feature_group_id ?? '',
  );
  const [sortOrder, setSortOrder] = useState(feature?.sort_order ?? 0);
  const [isNew, setIsNew] = useState(feature?.is_new ?? false);
  const [isActive, setIsActive] = useState(feature?.is_active ?? true);
  const [displayNameTranslations, setDisplayNameTranslations] = useState<TranslationMap>(
    feature?.display_name_translations ?? {},
  );
  const [descriptionTranslations, setDescriptionTranslations] = useState<TranslationMap>(
    feature?.description_translations ?? {},
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const nextErrors: Record<string, string> = {};
    if (!featureName.trim()) {
      nextErrors.feature_name = 'Feature name is required.';
    }
    if (!displayName.trim()) {
      nextErrors.display_name = 'Display name is required.';
    }
    if (!comparisonKey.trim()) {
      nextErrors.comparison_key = 'Comparison key is required.';
    }
    if (featureGroupId === '') {
      nextErrors.feature_group_id = 'Feature group is required.';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    const payload: FeatureCreate = {
      feature_name: featureName.trim(),
      display_name: displayName.trim(),
      display_name_translations: normalizeTranslations(displayNameTranslations),
      description: description.trim() || undefined,
      description_translations: normalizeTranslations(descriptionTranslations),
      comparison_key: comparisonKey.trim(),
      feature_group_id: Number(featureGroupId),
      sort_order: Number(sortOrder || 0),
      is_new: isNew,
      is_active: isActive,
    };

    if (feature) {
      await onSubmit(payload as FeatureUpdate);
      return;
    }

    await onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="details-form">
      <div className="details-section">
        <h2>Matrix Row Details</h2>
        <div className="details-grid">
          <label className="form-group">
            <span className="form-label">Feature Name *</span>
            <input
              type="text"
              className={`form-control ${errors.feature_name ? 'error' : ''}`}
              value={featureName}
              onChange={(event) => setFeatureName(event.target.value)}
              placeholder="website-matrix-document-generator-pa"
              disabled={Boolean(feature)}
            />
            {errors.feature_name ? (
              <small className="form-error">{errors.feature_name}</small>
            ) : (
              <small className="form-help">Stable internal identifier for this matrix row.</small>
            )}
          </label>

          <label className="form-group">
            <span className="form-label">Default Label *</span>
            <input
              type="text"
              className={`form-control ${errors.display_name ? 'error' : ''}`}
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="PA Documents"
            />
            {errors.display_name && <small className="form-error">{errors.display_name}</small>}
          </label>

          <label className="form-group">
            <span className="form-label">Comparison Key *</span>
            <input
              type="text"
              className={`form-control ${errors.comparison_key ? 'error' : ''}`}
              value={comparisonKey}
              onChange={(event) => setComparisonKey(event.target.value)}
              placeholder="documentGenerator.pa"
            />
            {errors.comparison_key ? (
              <small className="form-error">{errors.comparison_key}</small>
            ) : (
              <small className="form-help">Matches the raw comparison value key sent to the website.</small>
            )}
          </label>

          <label className="form-group">
            <span className="form-label">Feature Group *</span>
            <select
              className={`form-control ${errors.feature_group_id ? 'error' : ''}`}
              value={featureGroupId}
              onChange={(event) =>
                setFeatureGroupId(event.target.value ? Number(event.target.value) : '')
              }
            >
              <option value="">Select feature group</option>
              {featureGroups.map((group) => (
                <option key={group.feature_group_id} value={group.feature_group_id}>
                  {group.label} ({group.key})
                </option>
              ))}
            </select>
            {errors.feature_group_id && (
              <small className="form-error">{errors.feature_group_id}</small>
            )}
          </label>

          <label className="form-group">
            <span className="form-label">Display Order</span>
            <input
              type="number"
              className="form-control"
              min={0}
              value={sortOrder}
              onChange={(event) => setSortOrder(Number(event.target.value || 0))}
            />
          </label>

          <label className="form-group form-group-wide">
            <span className="form-label">Description</span>
            <textarea
              className="form-control"
              rows={4}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Describe the comparison row."
            />
          </label>

          <label className="form-group checkbox-row">
            <input
              type="checkbox"
              checked={isNew}
              onChange={(event) => setIsNew(event.target.checked)}
            />
            <span>Mark row as new</span>
          </label>

          <label className="form-group checkbox-row">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
            />
            <span>Matrix row is active</span>
          </label>
        </div>
      </div>

      <div className="details-section">
        <h2>Translations</h2>
        <TranslationFields
          title="Row Label Translations"
          value={displayNameTranslations}
          onChange={setDisplayNameTranslations}
          placeholder="Translated row label"
        />
        <TranslationFields
          title="Description Translations"
          value={descriptionTranslations}
          onChange={setDescriptionTranslations}
          placeholder="Translated description"
        />
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
          Close
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Saving...' : feature ? 'Save Matrix Row' : 'Create Matrix Row'}
        </button>
      </div>
    </form>
  );
}

const Features: React.FC = () => {
  const [activeTab, setActiveTab] = useState<FeatureTab>('rows');
  const [features, setFeatures] = useState<Feature[]>([]);
  const [featureGroups, setFeatureGroups] = useState<FeatureGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const [selectedFeatureGroup, setSelectedFeatureGroup] = useState<FeatureGroup | null>(null);
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [showFeatureGroupModal, setShowFeatureGroupModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    void loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [featureItems, groupItems] = await Promise.all([
        featureService.getFeatures({ limit: 1000, comparison_only: true }),
        featureService.getFeatureGroups({ limit: 1000 }),
      ]);
      setFeatures(featureItems);
      setFeatureGroups(groupItems);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load comparison matrix data.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFeatureGroup = async (payload: FeatureGroupCreate | FeatureGroupUpdate) => {
    try {
      setFormLoading(true);
      await featureService.createFeatureGroup(payload as FeatureGroupCreate);
      setShowFeatureGroupModal(false);
      setSelectedFeatureGroup(null);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create feature group.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateFeatureGroup = async (payload: FeatureGroupCreate | FeatureGroupUpdate) => {
    if (!selectedFeatureGroup) {
      return;
    }
    try {
      setFormLoading(true);
      await featureService.updateFeatureGroup(selectedFeatureGroup.feature_group_id, payload);
      setShowFeatureGroupModal(false);
      setSelectedFeatureGroup(null);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update feature group.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteFeatureGroup = async (featureGroup: FeatureGroup) => {
    if (!window.confirm(`Delete feature group "${featureGroup.label}"?`)) {
      return;
    }
    try {
      await featureService.deleteFeatureGroup(featureGroup.feature_group_id);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete feature group.');
    }
  };

  const handleCreateFeature = async (payload: FeatureCreate | FeatureUpdate) => {
    try {
      setFormLoading(true);
      await featureService.createFeature(payload as FeatureCreate);
      setShowFeatureModal(false);
      setSelectedFeature(null);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create matrix row.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateFeature = async (payload: FeatureCreate | FeatureUpdate) => {
    if (!selectedFeature) {
      return;
    }
    try {
      setFormLoading(true);
      await featureService.updateFeature(selectedFeature.feature_id, payload as FeatureUpdate);
      setShowFeatureModal(false);
      setSelectedFeature(null);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update matrix row.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteFeature = async (feature: Feature) => {
    if (!window.confirm(`Delete matrix row "${feature.display_name || feature.feature_name}"?`)) {
      return;
    }
    try {
      await featureService.deleteFeature(feature.feature_id);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete matrix row.');
    }
  };

  const filteredFeatureGroups = featureGroups.filter((featureGroup) => {
    const matchesSearch =
      !searchTerm ||
      featureGroup.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      featureGroup.label.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || String(featureGroup.is_active) === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredFeatures = features.filter((feature) => {
    const matchesSearch =
      !searchTerm ||
      feature.feature_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (feature.comparison_key ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (feature.display_name ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (feature.description ?? '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || String(feature.is_active) === statusFilter;
    const matchesGroup =
      !groupFilter ||
      String(feature.feature_group_id ?? '') === groupFilter;
    return matchesSearch && matchesStatus && matchesGroup;
  });

  return (
    <div className="features-page">
      <div className="page-header">
        <div>
          <h1>Comparison Matrix Management</h1>
          <p className="page-subtitle">
            Manage translated feature groups and matrix rows used to build the website comparison
            table. Missing row values render as `-` on the website.
          </p>
        </div>
        <div className="header-actions">
          {activeTab === 'rows' ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setSelectedFeature(null);
                setShowFeatureModal(true);
              }}
            >
              New Matrix Row
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setSelectedFeatureGroup(null);
                setShowFeatureGroupModal(true);
              }}
            >
              New Feature Group
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={loadData} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="btn btn-sm">
            x
          </button>
        </div>
      )}

      <div className="tabs-row">
        <button
          type="button"
          className={`tab-button ${activeTab === 'rows' ? 'active' : ''}`}
          onClick={() => setActiveTab('rows')}
        >
          Matrix Rows
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'groups' ? 'active' : ''}`}
          onClick={() => setActiveTab('groups')}
        >
          Feature Groups
        </button>
      </div>

      <div className="filters-panel">
        <label className="filter-group">
          <span>Search</span>
          <input
            type="text"
            className="form-control"
            placeholder={activeTab === 'rows' ? 'Search matrix rows...' : 'Search feature groups...'}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </label>

        {activeTab === 'rows' ? (
          <label className="filter-group">
            <span>Feature Group</span>
            <select
              className="form-control"
              value={groupFilter}
              onChange={(event) => setGroupFilter(event.target.value)}
            >
              <option value="">All feature groups</option>
              {featureGroups.map((featureGroup) => (
                <option key={featureGroup.feature_group_id} value={featureGroup.feature_group_id}>
                  {featureGroup.label}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <div className="filter-group filter-spacer" />
        )}

        <label className="filter-group">
          <span>Status</span>
          <select
            className="form-control"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="">All statuses</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </label>
      </div>

      <div className="table-shell">
        {loading ? (
          <div className="loading-state">
            <div className="spinner" />
            <p>Loading comparison matrix data...</p>
          </div>
        ) : activeTab === 'rows' ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Matrix Row</th>
                <th>Comparison Key</th>
                <th>Feature Group</th>
                <th>Translations</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFeatures.map((feature) => (
                <tr key={feature.feature_id}>
                  <td>
                    <div className="primary-text label-cell">
                      <span>{feature.display_name || feature.feature_name}</span>
                      {feature.is_new ? <span className="new-flag">New</span> : null}
                    </div>
                    <div className="secondary-text">{feature.feature_name}</div>
                    <div className="secondary-text">{feature.description || 'No description'}</div>
                  </td>
                  <td>
                    <code className="code-chip">{feature.comparison_key || '-'}</code>
                    <div className="secondary-text">Order: {feature.sort_order ?? 0}</div>
                  </td>
                  <td>
                    <span className="category-chip">
                      {feature.feature_group?.label || 'No group'}
                    </span>
                    <div className="secondary-text">{feature.feature_group?.key || '-'}</div>
                  </td>
                  <td>
                    <div className="primary-text">
                      Label: {summarizeTranslations(feature.display_name_translations)}
                    </div>
                    <div className="secondary-text">
                      Description: {summarizeTranslations(feature.description_translations)}
                    </div>
                  </td>
                  <td>
                    <span className={`status-chip ${feature.is_active ? 'active' : 'inactive'}`}>
                      {feature.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="actions-cell">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          setSelectedFeature(feature);
                          setShowFeatureModal(true);
                        }}
                      >
                        Details
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => void handleDeleteFeature(feature)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredFeatures.length === 0 && (
                <tr>
                  <td colSpan={6} className="empty-state">
                    No matrix rows match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Feature Group</th>
                <th>Key</th>
                <th>Translations</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFeatureGroups.map((featureGroup) => (
                <tr key={featureGroup.feature_group_id}>
                  <td>
                    <div className="primary-text">{featureGroup.label}</div>
                    <div className="secondary-text">Order: {featureGroup.sort_order}</div>
                  </td>
                  <td>
                    <code className="code-chip">{featureGroup.key}</code>
                  </td>
                  <td>{summarizeTranslations(featureGroup.label_translations)}</td>
                  <td>
                    <span
                      className={`status-chip ${featureGroup.is_active ? 'active' : 'inactive'}`}
                    >
                      {featureGroup.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="actions-cell">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          setSelectedFeatureGroup(featureGroup);
                          setShowFeatureGroupModal(true);
                        }}
                      >
                        Details
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => void handleDeleteFeatureGroup(featureGroup)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredFeatureGroups.length === 0 && (
                <tr>
                  <td colSpan={5} className="empty-state">
                    No feature groups match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {showFeatureModal && (
        <Modal
          title={selectedFeature ? 'Matrix Row Details' : 'Create Matrix Row'}
          onClose={() => {
            setSelectedFeature(null);
            setShowFeatureModal(false);
          }}
        >
          <FeatureRowForm
            feature={selectedFeature ?? undefined}
            featureGroups={featureGroups}
            onSubmit={selectedFeature ? handleUpdateFeature : handleCreateFeature}
            onCancel={() => {
              setSelectedFeature(null);
              setShowFeatureModal(false);
            }}
            loading={formLoading}
          />
        </Modal>
      )}

      {showFeatureGroupModal && (
        <Modal
          title={selectedFeatureGroup ? 'Feature Group Details' : 'Create Feature Group'}
          onClose={() => {
            setSelectedFeatureGroup(null);
            setShowFeatureGroupModal(false);
          }}
        >
          <FeatureGroupForm
            featureGroup={selectedFeatureGroup ?? undefined}
            onSubmit={selectedFeatureGroup ? handleUpdateFeatureGroup : handleCreateFeatureGroup}
            onCancel={() => {
              setSelectedFeatureGroup(null);
              setShowFeatureGroupModal(false);
            }}
            loading={formLoading}
          />
        </Modal>
      )}

      <style
        dangerouslySetInnerHTML={{
          __html: `
            .features-page {
              padding: 2rem;
              max-width: 1440px;
              margin: 0 auto;
            }

            .page-subtitle {
              margin-top: 0.5rem;
              color: #5f6c7b;
              max-width: 760px;
              line-height: 1.5;
            }

            .header-actions {
              display: flex;
              gap: 0.75rem;
              align-items: center;
            }

            .tabs-row {
              display: flex;
              gap: 0.75rem;
              margin-bottom: 1.5rem;
            }

            .tab-button {
              border: 1px solid #cfd7df;
              background: white;
              color: #274264;
              padding: 0.75rem 1rem;
              border-radius: 999px;
              cursor: pointer;
              font-weight: 600;
            }

            .tab-button.active {
              background: #14365d;
              border-color: #14365d;
              color: white;
            }

            .filters-panel {
              display: grid;
              grid-template-columns: repeat(3, minmax(0, 1fr));
              gap: 1rem;
              margin-bottom: 1.5rem;
              align-items: end;
            }

            .filter-group {
              display: flex;
              flex-direction: column;
              gap: 0.35rem;
            }

            .filter-spacer {
              min-height: 1px;
            }

            .table-shell {
              background: white;
              border-radius: 18px;
              border: 1px solid #dbe5ef;
              overflow: hidden;
              box-shadow: 0 18px 50px rgba(15, 23, 42, 0.06);
            }

            .loading-state {
              padding: 3rem;
              text-align: center;
              color: #5f6c7b;
            }

            .spinner {
              width: 2rem;
              height: 2rem;
              border-radius: 50%;
              border: 3px solid #dbe5ef;
              border-top-color: #14365d;
              margin: 0 auto 1rem;
              animation: spin 0.9s linear infinite;
            }

            .data-table {
              width: 100%;
              border-collapse: collapse;
            }

            .data-table th,
            .data-table td {
              padding: 1rem;
              border-bottom: 1px solid #edf2f7;
              vertical-align: top;
              text-align: left;
            }

            .data-table th {
              background: #f8fbff;
              color: #39516e;
              font-size: 0.8rem;
              letter-spacing: 0.04em;
              text-transform: uppercase;
            }

            .primary-text {
              color: #12263f;
              font-weight: 600;
            }

            .secondary-text {
              margin-top: 0.3rem;
              color: #6b7b8f;
              font-size: 0.88rem;
            }

            .label-cell {
              display: flex;
              align-items: center;
              gap: 0.5rem;
              flex-wrap: wrap;
            }

            .new-flag {
              display: inline-flex;
              align-items: center;
              padding: 0.2rem 0.55rem;
              border-radius: 999px;
              background: #fff3db;
              color: #9a6700;
              font-size: 0.72rem;
              font-weight: 700;
              text-transform: uppercase;
            }

            .code-chip,
            .category-chip {
              display: inline-flex;
              align-items: center;
              padding: 0.3rem 0.55rem;
              border-radius: 999px;
              background: #eef4fb;
              color: #24496f;
              font-size: 0.82rem;
              font-weight: 600;
            }

            .status-chip {
              display: inline-flex;
              align-items: center;
              padding: 0.3rem 0.55rem;
              border-radius: 999px;
              font-size: 0.8rem;
              font-weight: 600;
            }

            .status-chip.active {
              background: #e6f9ef;
              color: #147a4b;
            }

            .status-chip.inactive {
              background: #edf2f7;
              color: #5f6c7b;
            }

            .actions-cell {
              display: flex;
              gap: 0.5rem;
              flex-wrap: wrap;
            }

            .empty-state {
              padding: 2rem;
              text-align: center;
              color: #6b7b8f;
            }

            .details-form {
              display: flex;
              flex-direction: column;
              gap: 1.5rem;
            }

            .details-section {
              display: flex;
              flex-direction: column;
              gap: 1rem;
            }

            .details-grid {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 1rem;
            }

            .form-group {
              display: flex;
              flex-direction: column;
              gap: 0.4rem;
            }

            .form-group-wide {
              grid-column: 1 / -1;
            }

            .form-label {
              color: #304861;
              font-weight: 600;
            }

            .form-control.error {
              border-color: #d14343;
            }

            .form-error {
              color: #b42318;
              font-size: 0.82rem;
            }

            .form-help {
              color: #6b7b8f;
              font-size: 0.82rem;
            }

            .checkbox-row {
              flex-direction: row;
              align-items: center;
              gap: 0.6rem;
            }

            .translation-block {
              display: flex;
              flex-direction: column;
              gap: 0.75rem;
            }

            .translation-grid {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 0.85rem;
            }

            .translation-input {
              display: flex;
              flex-direction: column;
              gap: 0.35rem;
            }

            .form-actions {
              display: flex;
              justify-content: flex-end;
              gap: 0.75rem;
            }

            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }

            @media (max-width: 960px) {
              .filters-panel,
              .details-grid,
              .translation-grid {
                grid-template-columns: 1fr;
              }

              .actions-cell,
              .header-actions {
                flex-direction: column;
                align-items: stretch;
              }
            }
          `,
        }}
      />
    </div>
  );
};

export default Features;
